/**
 * Simple emoji picker component for chat.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Tooltip,
} from '@mui/joy';
import {
  Smiley as SentimentSatisfiedAlt,
  PawPrint as Pets,
  ForkKnife as Restaurant,
  Basketball as SportsBasketball,
  Car as DirectionsCar,
  Lightbulb as EmojiObjects,
  Hash as EmojiSymbols,
  Flag,
} from '@phosphor-icons/react';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
}

// Common emojis organized by category
const EMOJI_CATEGORIES = {
  smileys: {
    name: 'Smileys',
    icon: <SentimentSatisfiedAlt size={18} />,
    emojis: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊',
      '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😋', '😛', '😜',
      '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐',
      '😑', '😶', '😏', '😒', '🙄', '😬', '😮', '😯', '😲', '😳',
      '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖',
      '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬',
      '👍', '👎', '👏', '🙌', '🤝', '👋', '🤚', '✋', '🖐', '🖖',
      '👌', '🤌', '🤏', '✌', '🤞', '🤟', '🤘', '🤙', '💪', '🙏', '🔥',
    ],
  },
  animals: {
    name: 'Animals & Nature',
    icon: <Pets size={18} />,
    emojis: [
      '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
      '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆',
      '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋',
      '🌸', '🌷', '🌹', '🌺', '🌻', '🌼', '🌳', '🌴', '🌵', '🍀',
    ],
  },
  food: {
    name: 'Food & Drink',
    icon: <Restaurant size={18} />,
    emojis: [
      '🍎', '🍊', '🍋', '🍌', '🍇', '🍓', '🫐', '🍑', '🍒', '🍍',
      '🥭', '🍆', '🥑', '🥦', '🥒', '🌽', '🥕', '🧅', '🥔', '🍕',
      '🍔', '🍟', '🌭', '🥪', '🌮', '🌯', '🥗', '🍝', '🍜', '🍣',
      '🍱', '🍩', '🍪', '🎂', '🍰', '🧁', '☕', '🍵', '🍺', '🍷',
    ],
  },
  activities: {
    name: 'Activities',
    icon: <SportsBasketball size={18} />,
    emojis: [
      '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🎱', '🏓',
      '🏸', '🥊', '🥋', '⛳', '⛸', '🎣', '🤿', '🎿', '🛷', '🥌',
      '🎯', '🎮', '🎲', '🧩', '🎭', '🎨', '🎬', '🎤', '🎧', '🎼',
      '🎹', '🥁', '🎷', '🎺', '🎸', '🪕', '🎻', '🏆', '🥇', '🎖',
    ],
  },
  travel: {
    name: 'Travel & Places',
    icon: <DirectionsCar size={18} />,
    emojis: [
      '🚗', '🚕', '🚌', '🚎', '🚓', '🚑', '🚒', '🚐', '🚚', '🚛',
      '🚜', '🏎', '🏍', '🛵', '🚲', '🛴', '🚂', '🚆', '🚇', '✈',
      '🚀', '🛸', '🚁', '⛵', '🚢', '🗽', '🗼', '🏰', '🏯', '🏟',
      '🏠', '🏢', '🏥', '🏦', '⛪', '🕌', '🌁', '🌃', '🌆', '🌉',
    ],
  },
  objects: {
    name: 'Objects',
    icon: <EmojiObjects size={18} />,
    emojis: [
      '⌚', '📱', '💻', '⌨', '🖥', '🖨', '🖱', '💾', '📀', '📷',
      '📹', '🎥', '📞', '☎', '📺', '📻', '🎙', '⏰', '🔋', '🔌',
      '💡', '🔦', '🕯', '💰', '💳', '💎', '🔨', '🔧', '🔩', '⚙',
      '📌', '📎', '📏', '📐', '✂', '📦', '📫', '📪', '🔑', '🔐',
    ],
  },
  symbols: {
    name: 'Symbols',
    icon: <EmojiSymbols size={18} />,
    emojis: [
      '❤', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
      '❣', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮',
      '✝', '☪', '🕉', '☸', '✡', '🔯', '🕎', '☯', '☦', '🛐',
      '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑',
      '✅', '❌', '❓', '❗', '✖', '➕', '➖', '➗', '💯', '🔢',
    ],
  },
  flags: {
    name: 'Flags',
    icon: <Flag size={18} />,
    emojis: [
      '🏁', '🚩', '🎌', '🏴', '🏳', '🏳️‍🌈', '🏴‍☠️',
      '🇺🇸', '🇬🇧', '🇨🇦', '🇦🇺', '🇩🇪', '🇫🇷', '🇪🇸', '🇮🇹',
      '🇯🇵', '🇰🇷', '🇨🇳', '🇮🇳', '🇧🇷', '🇲🇽', '🇷🇺', '🇿🇦',
    ],
  },
};

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onEmojiSelect }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const categories = Object.entries(EMOJI_CATEGORIES);

  return (
    <Box
      sx={{
        width: 320,
        maxHeight: 400,
        bgcolor: 'background.surface',
        borderRadius: 'md',
        boxShadow: 'lg',
        overflow: 'hidden',
      }}
    >
      <Tabs
        value={selectedTab}
        onChange={(_, value) => setSelectedTab(value as number)}
        sx={{ bgcolor: 'background.level1' }}
      >
        <TabList
          sx={{
            justifyContent: 'space-around',
            '--ListItem-minHeight': '36px',
          }}
        >
          {categories.map(([key, category], index) => (
            <Tab key={key} value={index}>
              <Tooltip title={category.name} placement="top">
                {category.icon}
              </Tooltip>
            </Tab>
          ))}
        </TabList>

        {categories.map(([key, category], index) => (
          <TabPanel
            key={key}
            value={index}
            sx={{
              p: 1,
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            <Typography level="body-xs" sx={{ mb: 1, color: 'text.secondary' }}>
              {category.name}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: 0.25,
              }}
            >
              {category.emojis.map((emoji, i) => (
                <Button
                  key={`${key}-${i}`}
                  variant="plain"
                  color="neutral"
                  onClick={() => onEmojiSelect(emoji)}
                  sx={{
                    minWidth: 0,
                    p: 0.5,
                    fontSize: '1.25rem',
                    '&:hover': {
                      bgcolor: 'neutral.100',
                    },
                  }}
                >
                  {emoji}
                </Button>
              ))}
            </Box>
          </TabPanel>
        ))}
      </Tabs>
    </Box>
  );
};

export default EmojiPicker;
